import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "stenzia_cookie_consent";

export function getCookieConsent() {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY); // "accepted" | "rejected" | null
}

/**
 * Lightweight cookie consent banner.
 * Stores user choice in localStorage; analytics scripts read it before initialising.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) {
      const t = setTimeout(() => setVisible(true), 1400);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setVisible(false);
    window.dispatchEvent(new CustomEvent("stenzia:consent", { detail: { value: "accepted" } }));
  };
  const reject = () => {
    localStorage.setItem(STORAGE_KEY, "rejected");
    setVisible(false);
    window.dispatchEvent(new CustomEvent("stenzia:consent", { detail: { value: "rejected" } }));
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          data-testid="cookie-banner"
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:max-w-md z-[120] bg-charcoal text-white rounded-2xl shadow-2xl border border-white/10 p-5 md:p-6 flex flex-col md:flex-row items-start gap-4"
        >
          <div className="w-10 h-10 shrink-0 rounded-full bg-stenzia-green/15 text-stenzia-green flex items-center justify-center">
            <Cookie size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm tracking-tight text-white">We use cookies.</div>
            <p className="mt-1 text-[12px] leading-relaxed text-white/65">
              We use minimal analytics cookies to understand how visitors use our site. You can accept or
              reject analytics — essential cookies are always on.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={accept}
                data-testid="cookie-accept"
                className="text-[12px] font-medium px-4 py-2 rounded-full bg-stenzia-green text-white hover:bg-white hover:text-charcoal transition-colors"
              >
                Accept
              </button>
              <button
                onClick={reject}
                data-testid="cookie-reject"
                className="text-[12px] font-medium px-4 py-2 rounded-full border border-white/20 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
